import { Share2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AutodeskPushMenuItem } from "@/components/records/autodesk-push-menu-item/autodesk-push-menu-item";
import { autodeskPushIndicator } from "@/components/records/autodesk-push-menu-item/autodesk-push-indicator";
import { useRecordAutodeskPush } from "@/hooks/use-autodesk";
import { cn } from "@/lib/utils";

interface Props {
  projectId: string;
  recordId: string;
  hasActiveConnection: boolean;
  /** Whether this device can hand the photo to the OS share sheet. */
  canShare: boolean;
  isSharing: boolean;
  onShare: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * The record's outward-facing actions, collapsed behind one icon.
 *
 * "Send to Autodesk" used to sit in the view-actions bar as its own full-width
 * button. Together with the issue button that pushed the bar ~90px past a
 * phone's screen, clipping itself off the right edge. Both destinations now
 * live under Share, which costs one tap and buys the bar back.
 *
 * See [[record-share-menu.spec]].
 */
export function RecordShareMenu({
  projectId,
  recordId,
  hasActiveConnection,
  canShare,
  isSharing,
  onShare,
  disabled = false,
  disabledReason,
}: Props) {
  // Read here as well as inside the menu item so the closed trigger can carry a
  // status dot. Same query key, so TanStack serves both from one request, and
  // both derive their meaning from `autodeskPushIndicator` — the dot and the
  // row can never disagree.
  const { data: push, error } = useRecordAutodeskPush(
    projectId,
    recordId,
    hasActiveConnection,
  );
  const indicator = hasActiveConnection
    ? autodeskPushIndicator(push, error)
    : "hidden";
  const autodeskAvailable = indicator !== "hidden";

  if (!canShare && !autodeskAvailable) return null;

  const triggerClasses = "h-[46px] basis-[50px] grow-0 p-0";

  // Only one destination, and it's the OS share sheet: keep today's single tap
  // rather than making people open a menu to reach the one thing in it. This is
  // the common case — most projects have no Forma connection.
  //
  // The reverse (Autodesk available, no share sheet) deliberately does NOT
  // collapse: a Share icon that silently publishes an issue to Forma, with no
  // label in between, is not something to fire off one unlabelled tap.
  if (canShare && !autodeskAvailable) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={onShare}
        loading={isSharing}
        disabled={isSharing}
        className={triggerClasses}
        aria-label="Share"
      >
        <Share2 className="h-[17px] w-[17px]" />
      </Button>
    );
  }

  const dot =
    indicator === "sending"
      ? "bg-muted-foreground animate-pulse"
      : indicator === "linked"
        ? "bg-primary"
        : indicator === "failed"
          ? "bg-destructive"
          : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          loading={isSharing}
          className={cn(triggerClasses, "relative")}
          aria-label="Share"
        >
          <Share2 className="h-[17px] w-[17px]" />
          {dot && (
            <span
              aria-hidden="true"
              data-testid="autodesk-status-dot"
              className={cn(
                "absolute right-1.5 top-1.5 h-2 w-2 rounded-full ring-2 ring-background",
                dot,
              )}
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      {/* Above the bar, per the bar being pinned to the bottom of the screen. */}
      <DropdownMenuContent side="top" align="start" className="min-w-56">
        {canShare && (
          <DropdownMenuItem
            onSelect={onShare}
            disabled={isSharing}
            aria-label="Share to others"
          >
            <Share2 className="h-4 w-4" />
            Share to others
          </DropdownMenuItem>
        )}
        <AutodeskPushMenuItem
          projectId={projectId}
          recordId={recordId}
          hasActiveConnection={hasActiveConnection}
          disabled={disabled}
          disabledReason={disabledReason}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
